import { useState, useEffect } from 'react';
import {
  Box, Button, Card, CardContent, CardActions, CardHeader, Divider, Grid, TextField, MenuItem, Select, FormControl, InputLabel, IconButton, Typography,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { AddCircle, Delete, Close } from '@mui/icons-material';
import axios from '../../../../services/ApiService';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../../support/LoadingSpinner';
import { Helmet } from 'react-helmet-async';
import { ScoreRange, Workflow } from '../../../types/ProgramTypes';
import { Assessment } from '../../../types/AssessmentTypes';
import { BVTConstants } from '../../../../utils/BVTConstants';
import Swal from 'sweetalert2';

const WorkflowForm = () => {
  const [programId, setProgramId] = useState(0);
  const [programName, setProgramName] = useState('');
  const [workflowId, setWorkflowId] = useState(0);
  const [workflowName, setWorkflowName] = useState('');
  const [description, setDescription] = useState('');
  const [workflowType, setWorkflowType] = useState(BVTConstants.WORKFLOW_TYPE_PRIMARY);
  const [selfSubscribe, setSelfSubscribe] = useState(false);
  const [addInstructions, setAddInstructions] = useState(false);
  const [instructions, setInstructions] = useState('');
  const [showInstrPostCompletion, setShowInstrPostCompletion] = useState(false);
  const [steps, setSteps] = useState([]);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [isEdit, setIsEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { programId: passedProgramId, programName: passedProgramName, workflowId: passedWorkflowId, workflow } = location.state || {};

  useEffect(() => {
    if (passedProgramId) {
      setProgramId(passedProgramId);
      setProgramName(passedProgramName || '');
    }
    
    if (passedWorkflowId && workflow) {
      setWorkflowId(passedWorkflowId);
      setWorkflowName(workflow.name || '');
      setDescription(workflow.description || '');
      setWorkflowType(workflow.type || BVTConstants.WORKFLOW_TYPE_PRIMARY);
      setSelfSubscribe(workflow.selfSubscribe || false);
      setInstructions(workflow.instructions || '');
      setShowInstrPostCompletion(workflow.showInstructionsAfterCompletion || false);
      setAddInstructions(!!workflow.instructions);
      setSteps(workflow.steps || []);
      setIsEdit(true);
    }
  }, [passedProgramId, passedProgramName, passedWorkflowId, workflow]);

  const fetchAssessments = async () => {
    try {
      const response = await axios.get('/getAssessmentsViews');
      if (Array.isArray(response.data)) {
        setAssessments(response.data);
      }
    } catch (error) {
      console.error('Error fetching assessments.');
    }
  };

  useEffect(() => {
    fetchAssessments();
  }, []);

  // Set total score for assessment steps when editing and assessments are loaded
  useEffect(() => {
    if (isEdit && assessments.length > 0 && steps.length > 0) {
      const updatedSteps = steps.map(step => {
        if (step.stepActionType === BVTConstants.WORKFLOW_ACTION_TYPE_ASSESSMENT && step.assessmentId) {
          const selectedAssessment = assessments.find(assessment => assessment.id === step.assessmentId);
          if (selectedAssessment) {
            // Ensure fixed score ranges exist for assessment steps
            const fixedScoreRanges = [
              { id: Date.now() + 1, name: BVTConstants.SCORE_RANGE_POOR, minVal: 0, maxVal: 0, error: '' },
              { id: Date.now() + 2, name: BVTConstants.SCORE_RANGE_NEED_IMPROVEMENT, minVal: 0, maxVal: 0, error: '' },
              { id: Date.now() + 3, name: BVTConstants.SCORE_RANGE_STRONG, minVal: 0, maxVal: 0, error: '' },
              { id: Date.now() + 4, name: BVTConstants.SCORE_RANGE_EXCELLENT, minVal: 0, maxVal: 0, error: '' }
            ];
            
            return {
              ...step,
              totalScore: selectedAssessment.totalScore,
              scoreRanges: step.scoreRanges && step.scoreRanges.length > 0 ? step.scoreRanges : fixedScoreRanges
            };
          }
        }
        return step;
      });
      setSteps(updatedSteps);
    }
  }, [isEdit, assessments, steps.length]);

  const handleAddStep = () => {
    const seqId = steps.length + 1;
    setSteps([...steps, {
      id: Date.now(),
      seqId,
      stepName: '',
      instructions: '',
      stepActionOn: BVTConstants.USER_TYPE_BUSINESS,
      stepActionType: BVTConstants.WORKFLOW_ACTION_TYPE_CUSTOM,
      assessmentId: 0,
      assessment: {},
      totalScore: 0,
      scoreRanges: [],
    }]);
  };

  const handleDeleteStep = (stepIndex: number) => {
    const updatedSteps = steps.filter((_, i) => i !== stepIndex);
    const reorderedSteps = updatedSteps.map((step, index) => ({
      ...step,
      seqId: index + 1,
    }));
    setSteps(reorderedSteps);
  };

  const handleAssessmentChange = (stepIndex: number, assessmentId: number) => {
    const updatedSteps = [...steps];
    const selectedAssessment = assessments.find(assessment => assessment.id === assessmentId);
    updatedSteps[stepIndex].assessmentId = assessmentId;
    updatedSteps[stepIndex].totalScore = selectedAssessment ? selectedAssessment.totalScore : 0;
    
    // Add fixed score ranges if they don't exist
    if (!updatedSteps[stepIndex].scoreRanges || updatedSteps[stepIndex].scoreRanges.length === 0) {
      updatedSteps[stepIndex].scoreRanges = [
        { id: Date.now() + 1, name: BVTConstants.SCORE_RANGE_POOR, minVal: 0, maxVal: 0, error: '' },
        { id: Date.now() + 2, name: BVTConstants.SCORE_RANGE_NEED_IMPROVEMENT, minVal: 0, maxVal: 0, error: '' },
        { id: Date.now() + 3, name: BVTConstants.SCORE_RANGE_STRONG, minVal: 0, maxVal: 0, error: '' },
        { id: Date.now() + 4, name: BVTConstants.SCORE_RANGE_EXCELLENT, minVal: 0, maxVal: 0, error: '' }
      ];
    }
    
    setSteps(updatedSteps);
  };

  const handleActionTypeChange = (stepIndex: number, actionType: string) => {
    const updatedSteps = [...steps];
    updatedSteps[stepIndex].stepActionType = actionType;
    
    // If changing to assessment type, add score ranges
    if (actionType === BVTConstants.WORKFLOW_ACTION_TYPE_ASSESSMENT) {
      if (!updatedSteps[stepIndex].scoreRanges || updatedSteps[stepIndex].scoreRanges.length === 0) {
        updatedSteps[stepIndex].scoreRanges = [
          { id: Date.now() + 1, name: BVTConstants.SCORE_RANGE_POOR, minVal: 0, maxVal: 0, error: '' },
          { id: Date.now() + 2, name: BVTConstants.SCORE_RANGE_NEED_IMPROVEMENT, minVal: 0, maxVal: 0, error: '' },
          { id: Date.now() + 3, name: BVTConstants.SCORE_RANGE_STRONG, minVal: 0, maxVal: 0, error: '' },
          { id: Date.now() + 4, name: BVTConstants.SCORE_RANGE_EXCELLENT, minVal: 0, maxVal: 0, error: '' }
        ];
      }
    } else {
      // If changing away from assessment type, clear score ranges
      updatedSteps[stepIndex].scoreRanges = [];
      updatedSteps[stepIndex].assessmentId = 0;
      updatedSteps[stepIndex].totalScore = 0;
    }
    
    setSteps(updatedSteps);
  };

  const validateAllScoreRanges = (scoreRanges: ScoreRange[], totalScore: number) => {
    // Clear all previous errors
    scoreRanges.forEach(range => range.error = '');

    // Check each range for individual issues
    for (let i = 0; i < scoreRanges.length; i++) {
      const range = scoreRanges[i];
      
      // Check if min is less than or equal to max
      if (range.minVal > range.maxVal) {
        range.error = 'Min value must be less than or equal to max value';
        continue;
      }

      // Check if range is within bounds
      if (range.minVal < 0 || range.maxVal > totalScore) {
        range.error = `Range must be between 0 and ${totalScore}`;
        continue;
      }

      // Check for overlaps with other ranges
      const hasOverlap = scoreRanges.some((otherRange, j) => {
        if (i === j) return false;
        return !(range.maxVal < otherRange.minVal || range.minVal > otherRange.maxVal);
      });

      if (hasOverlap) {
        range.error = 'Overlapping with another range';
        continue;
      }
    }

    // Check for gaps by ensuring all scores from 0 to totalScore are covered
    const allRanges = scoreRanges.map((range, i) => ({
      min: range.minVal,
      max: range.maxVal,
      index: i
    }));

    // Sort ranges by min value
    allRanges.sort((a, b) => a.min - b.min);

    // Check if ranges cover from 0 to totalScore without gaps
    let currentMax = -1;
    for (const range of allRanges) {
      if (range.min > currentMax + 1) {
        // Found a gap - mark all ranges with gap error
        scoreRanges.forEach((scoreRange, idx) => {
          if (!scoreRange.error) {
            scoreRange.error = `Gap detected: missing scores between ${currentMax + 1} and ${range.min - 1}`;
          }
        });
        return;
      }
      currentMax = Math.max(currentMax, range.max);
    }

    // Check if the last range extends to totalScore
    if (currentMax < totalScore) {
      scoreRanges.forEach((scoreRange, idx) => {
        if (!scoreRange.error) {
          scoreRange.error = `Gap detected: missing scores between ${currentMax + 1} and ${totalScore}`;
        }
      });
    }
  };

  const handleScoreRangeChange = (
    stepIndex: number,
    rangeIndex: number,
    name: string,
    min: number,
    max: number
  ) => {
    const updatedSteps = [...steps];
    const scoreRanges: ScoreRange[] = [...(updatedSteps[stepIndex].scoreRanges || [])];

    // Update the specific range
    scoreRanges[rangeIndex].name = name;
    scoreRanges[rangeIndex].minVal = min;
    scoreRanges[rangeIndex].maxVal = max;

    const totalScore = updatedSteps[stepIndex].totalScore || 0;

    // Validate all ranges
    validateAllScoreRanges(scoreRanges, totalScore);

    updatedSteps[stepIndex].scoreRanges = scoreRanges;
    setSteps(updatedSteps);
  };



  const formatAmount = (value: number | undefined): string => {
    if (value === undefined || value === null || isNaN(value)) return '';
    return value.toString();
  };

  const calculateWorkflowCost = (workflowSteps: any[]) => {
    return workflowSteps
      .filter(step => step.stepActionType === BVTConstants.WORKFLOW_ACTION_TYPE_PAYMENT)
      .reduce((total, step) => total + (step.cost || 0), 0);
  };

  const handleSave = async (isDraft: boolean = false) => {
    // Validate workflow name
    if (!workflowName.trim()) {
        toast.error('Workflow name is required');
        return;
    }

    // Validate workflow description
    if (!description.trim()) {
        toast.error('Workflow description is required');
        return;
    }

    // Validate instructions if addInstructions is checked
    if (addInstructions && !instructions.trim()) {
        toast.error('Instructions are required when Add Instructions is checked');
        return;
    }

    // Validate workflow type
    if (!workflowType || ![BVTConstants.WORKFLOW_TYPE_PRIMARY, BVTConstants.WORKFLOW_TYPE_ADD_ON].includes(workflowType)) {
        toast.error('Workflow type is required');
        return;
    }

    // Validate minimum steps
    if (!steps || steps.length === 0) {
        toast.error('At least one step is required');
        return;
    }

    // Validate steps
    for (let j = 0; j < steps.length; j++) {
        const step = steps[j];
        
        // Validate step name
        if (!step.stepName.trim()) {
            toast.error(`Step ${j + 1} name is required`);
            return;
        }

        // Validate assessment steps
        if (step.stepActionType === BVTConstants.WORKFLOW_ACTION_TYPE_ASSESSMENT) {
            // Check if assessment is selected
            if (!step.assessmentId || step.assessmentId === 0) {
                toast.error(`Please select an assessment for step ${j + 1}`);
                return;
            }

            // Check if score ranges exist
            if (!step.scoreRanges || step.scoreRanges.length === 0) {
                toast.error(`Please add score ranges for assessment in step ${j + 1}`);
                return;
            }

            // Validate score ranges
            const ranges = step.scoreRanges;
            let coveredScores = new Set();
            
            // Check for empty range names
            for (let range of ranges) {
                if (!range.name.trim()) {
                    toast.error(`Please provide a name for all score ranges in step ${j + 1}`);
                    return;
                }
            }

            // Check for overlapping ranges and coverage
            for (let range of ranges) {
                if (range.error) {
                    toast.error(`Please fix the score range errors in step ${j + 1}`);
                    return;
                }

                // Add all scores in this range to the covered scores set
                for (let score = range.minVal; score <= range.maxVal; score++) {
                    coveredScores.add(score);
                }
            }

            // Check if all scores from 0 to totalScore are covered
            for (let score = 0; score <= step.totalScore; score++) {
                if (!coveredScores.has(score)) {
                    toast.error(`Score ranges must cover all possible scores (0 to ${step.totalScore}) in step ${j + 1}`);
                    return;
                }
            }
        }

        // Validate payment steps
        if (step.stepActionType === BVTConstants.WORKFLOW_ACTION_TYPE_PAYMENT) {
            // Check if Action On is Admin (payment not allowed for admin)
            if (step.stepActionOn === BVTConstants.USER_TYPE_ADMIN) {
                toast.error(`Payment steps are not allowed when Action On is Admin in step ${j + 1}`);
                return;
            }
            
            // Check if payment amount is valid
            if (!step.cost || step.cost <= 0) {
                toast.error(`Please enter a valid payment amount for step ${j + 1}`);
                return;
            }
        }
    }

    const workflowData: Workflow = {
      id: workflowId,
      name: workflowName,
      description: description,
      instructions: addInstructions ? instructions : '',
      showInstructionsAfterCompletion: addInstructions ? showInstrPostCompletion : false,
      type: workflowType,
      selfSubscribe: selfSubscribe,
      payment: calculateWorkflowCost(steps),
      status: isDraft ? 'DRAFT' : BVTConstants.ACTIVE,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      steps,
    };

    setLoading(true);
    try{
      if(isEdit){
        await axios.post('/updateWorkflow', workflowData);
        console.log('Workflow updated successfully');
        toast.success("Workflow Updated Successfully.");
        navigate(-1);
      }else{
        await axios.post(`/createWorkflow/${programId}`, workflowData);
        console.log('Workflow saved successfully');
        toast.success(isDraft ? "Workflow Draft Saved Successfully." : "Workflow Submitted Successfully.");
        navigate(-1);
      }
    } catch (error) {
      console.error('Error saving workflow.');
      toast.error('Error saving workflow. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitWorkflow = async () => {
    const result = await Swal.fire({
      title: 'Submit Workflow?',
      text: 'Once submitted, this workflow cannot be edited. Are you sure you want to submit?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, Submit!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      await handleSave(false);
    }
  };

  const handleSaveDraft = async () => {
    await handleSave(true);
  };

  return (
    <>
    <Helmet><title>BVT Admin - Workflows</title></Helmet>
    <Card>
      <CardHeader 
        title={`${isEdit ? 'Update' : 'Create'} Workflow`} 
        subheader={programName ? `Program: ${programName}` : ''}
        action={
          <IconButton onClick={() => navigate(-1)}>
            <Close />
          </IconButton>
        }
        sx={{ paddingBottom: 1 }} 
      />
      <Divider />
      <CardContent sx={{ paddingTop: 2 }}>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              required
              label="Workflow Name"
              value={workflowName}
              onChange={(e) => setWorkflowName(e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              required
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Grid>
        </Grid>

        <Box mt={2}>
          <FormControlLabel
            control={
              <Checkbox
                checked={addInstructions}
                onChange={(e) => setAddInstructions(e.target.checked)}
                color="primary"
              />
            }
            label="Add Instructions"
          />
        </Box>

        {addInstructions && (
          <Box mt={2}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" mb={1}>
                  <Typography variant="body2" sx={{ mr: 2 }}>
                    Instructions
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showInstrPostCompletion}
                        onChange={(e) => setShowInstrPostCompletion(e.target.checked)}
                        color="primary"
                        size="small"
                      />
                    }
                    label="Show after completion"
                  />
                </Box>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Instructions"
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  placeholder="Enter workflow instructions..."
                  inputProps={{ maxLength: 1000 }}
                />
                <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                  {instructions.length}/1000 characters
                </Typography>
              </Grid>
            </Grid>
          </Box>
        )}

        <Box mt={3}>
          <Card sx={{ mt: 2 }}>
            <CardContent sx={{ paddingTop: 0 }}>
              <Grid container spacing={1}>
                <Grid item xs={12} ml={1}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selfSubscribe}
                        onChange={(e) => setSelfSubscribe(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Allow Self Subscription"
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={workflowType}
                      onChange={(e) => setWorkflowType(e.target.value as string)}
                      sx={{ '& .MuiSelect-select': { border: 'none' } }}
                      label='Type'
                    >
                      <MenuItem value={BVTConstants.WORKFLOW_TYPE_PRIMARY}>Primary</MenuItem>
                      <MenuItem value={BVTConstants.WORKFLOW_TYPE_ADD_ON}>Add-on</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Total Cost"
                    type="number"
                    value={formatAmount(calculateWorkflowCost(steps))}
                    InputProps={{
                      readOnly: true,
                    }}
                    helperText="Total sum of all payment steps"
                  />
                </Grid>
              </Grid>
            </CardContent>

            <CardContent sx={{ paddingBottom: 1 }}>
              {steps.map((step, stepIndex) => (
                <Card key={step.id} sx={{ mt: 1 }}>
                  <CardHeader
                    title={
                      <Box display="flex" flexDirection="column">
                        <Typography variant="body2" sx={{ marginBottom: 1 }}>
                          Step-{stepIndex + 1}
                        </Typography>
                        <TextField
                          fullWidth
                          label="Step Name"
                          value={step.stepName}
                          onChange={(e) => {
                            const updatedSteps = [...steps];
                            updatedSteps[stepIndex].stepName = e.target.value;
                            setSteps(updatedSteps);
                          }}
                          inputProps={{ maxLength: 50 }}
                        />
                      </Box>
                    }
                    action={
                      <IconButton onClick={() => handleDeleteStep(stepIndex)}>
                        <Delete />
                      </IconButton>
                    }
                    sx={{ paddingBottom: 1 }}
                  />
                  <CardContent sx={{ paddingBottom: 1 }}>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Instructions"
                          value={step.instructions}
                          onChange={(e) => {
                            const updatedSteps = [...steps];
                            updatedSteps[stepIndex].instructions = e.target.value;
                            setSteps(updatedSteps);
                          }}
                          inputProps={{ maxLength: 100 }}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <FormControl fullWidth>
                          <InputLabel>Action On</InputLabel>
                          <Select
                            value={step.stepActionOn}
                            onChange={(e) => {
                              const updatedSteps = [...steps];
                              const newActionOn = e.target.value;
                              updatedSteps[stepIndex].stepActionOn = newActionOn;
                              
                              // If switching to Admin and current action type is Payment, switch to Custom
                              if (newActionOn === BVTConstants.USER_TYPE_ADMIN && 
                                  updatedSteps[stepIndex].stepActionType === BVTConstants.WORKFLOW_ACTION_TYPE_PAYMENT) {
                                updatedSteps[stepIndex].stepActionType = BVTConstants.WORKFLOW_ACTION_TYPE_CUSTOM;
                                updatedSteps[stepIndex].cost = 0; // Clear payment amount
                              }
                              
                              setSteps(updatedSteps);
                            }}
                            label='Action On'
                            sx={{ '& .MuiSelect-select': { border: 'none' } }}
                          >
                            <MenuItem value={BVTConstants.USER_TYPE_BUSINESS}>{BVTConstants.USER_TYPE_BUSINESS}</MenuItem>
                            <MenuItem value={BVTConstants.USER_TYPE_AGENT}>{BVTConstants.USER_TYPE_AGENT}</MenuItem>
                            <MenuItem value={BVTConstants.USER_TYPE_ADMIN}>{BVTConstants.USER_TYPE_ADMIN}</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={3}>
                        <FormControl fullWidth>
                          <InputLabel>Action Type</InputLabel>
                          <Select
                            value={step.stepActionType}
                            onChange={(e) => handleActionTypeChange(stepIndex, e.target.value)}
                            label='Action Type'
                            sx={{ '& .MuiSelect-select': { border: 'none' } }}
                          >
                            <MenuItem value={BVTConstants.WORKFLOW_ACTION_TYPE_ASSESSMENT}>{BVTConstants.WORKFLOW_ACTION_TYPE_ASSESSMENT}</MenuItem>
                            {step.stepActionOn !== BVTConstants.USER_TYPE_ADMIN && (
                              <MenuItem value={BVTConstants.WORKFLOW_ACTION_TYPE_PAYMENT}>{BVTConstants.WORKFLOW_ACTION_TYPE_PAYMENT}</MenuItem>
                            )}
                            <MenuItem value={BVTConstants.WORKFLOW_ACTION_TYPE_CUSTOM}>{BVTConstants.WORKFLOW_ACTION_TYPE_CUSTOM}</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                    {step.stepActionType === BVTConstants.WORKFLOW_ACTION_TYPE_ASSESSMENT && (
                      <Box mt={1}>
                        <Grid container spacing={1} sx={{marginTop: 1}}>
                          <Grid item xs={6}>
                            <FormControl fullWidth>
                              <InputLabel>Select Assessment</InputLabel>
                              <Select
                                value={step.assessmentId}
                                onChange={(e) => handleAssessmentChange(stepIndex, e.target.value as number)}
                                sx={{ '& .MuiSelect-select': { border: 'none' } }}
                                label='Select Assessment'
                              >
                                {assessments.map((assessment) => (
                                  <MenuItem key={assessment.id} value={assessment.id}>
                                    {assessment.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={6} sx={{marginTop: 1}}>
                            <Typography variant="body2">
                              Total Score: {step.totalScore || 0}
                            </Typography>
                          </Grid>
                        </Grid>
                        <Box mt={2}>
                          <Typography variant="subtitle2">Score Ranges & Grades</Typography>
                          {step.scoreRanges?.map((range, rangeIndex) => (
                            <Grid container spacing={1} key={rangeIndex} mt={1}>
                              <Grid item xs={3}>
                                <TextField
                                  fullWidth
                                  label="Range Name"
                                  value={range.name}
                                  InputProps={{
                                    readOnly: true,
                                  }}
                                  sx={{
                                    '& .MuiInputBase-input.Mui-readOnly': {
                                      backgroundColor: '#f5f5f5',
                                      color: '#666',
                                    }
                                  }}
                                />
                              </Grid>
                              <Grid item xs={2}>
                                <TextField
                                  fullWidth
                                  label="Min"
                                  type="number"
                                  value={range.minVal}
                                  onChange={(e) => handleScoreRangeChange(stepIndex, rangeIndex, range.name, Number(e.target.value), range.maxVal)}
                                />
                              </Grid>
                              <Grid item xs={2}>
                                <TextField
                                  fullWidth
                                  label="Max"
                                  type="number"
                                  value={range.maxVal}
                                  onChange={(e) => handleScoreRangeChange(stepIndex, rangeIndex, range.name, range.minVal, Number(e.target.value))}
                                />
                              </Grid>
                              <Grid item xs={4}>
                                {range.error && <Typography color="error">{range.error}</Typography>}
                              </Grid>
                            </Grid>
                          ))}

                        </Box>
                      </Box>
                    )}
                    {step.stepActionType === BVTConstants.WORKFLOW_ACTION_TYPE_PAYMENT && (
                      <Box mt={2}>
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              label="Payment Amount"
                              type="number"
                              inputProps={{
                                step: "0.10",
                                min: "0",
                                lang: "en-US"
                              }}
                              value={formatAmount(step.cost)}
                              onChange={(e) => {
                                const updatedSteps = [...steps];
                                const value = e.target.value;
                                updatedSteps[stepIndex].cost = value === '' ? 0 : Number(parseFloat(value).toFixed(2));
                                setSteps(updatedSteps);
                              }}
                              helperText="Enter the payment amount (up to 2 decimal places)"
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ))}
              <Button startIcon={<AddCircle />} onClick={handleAddStep} sx={{ mt: 1 }}>
                Add Step
              </Button>
            </CardContent>
          </Card>
        </Box>
      </CardContent>
      <Divider />
      <CardActions sx={{ justifyContent: 'flex-end' }}>
        <Button variant="contained" color='error' onClick={() => navigate(-1)}>Cancel</Button>
        <Button variant="outlined" onClick={handleSaveDraft}>
          Save Draft
        </Button>
        <Button variant="contained" onClick={handleSubmitWorkflow}>
          Submit Workflow
        </Button>
      </CardActions>
    </Card>
    <LoadingSpinner loading={loading} />
    </>
  );
};

export default WorkflowForm;
